import type { User } from '../shared/api-client';
import { getManagedRole, MANAGED_ROLES, type ManagedRole } from './managed-role-ui-registry';

// True if the user holds any tier of the given managed role, or is an admin
// (unless the role sets `adminsNeedGrant`). Pass `tierId` to require a specific tier.
export function userHasManagedRole(
  user: User | null,
  roleId: string,
  tierId?: string
): boolean {
  if (!user) return false;
  if (user.groups.includes('admin') && !getManagedRole(roleId)?.adminsNeedGrant) return true;
  const heldTiers = user.managedRoles?.[roleId];
  if (!heldTiers || heldTiers.length === 0) return false;
  if (tierId === undefined) return true;
  return heldTiers.includes(tierId);
}

// Roles the user can grant to others: those with a lower tier to hand out, where
// the user holds the top tier. Mirrors canManageTier in managed-role-routes.ts —
// a top-tier holder may grant only strictly-lower tiers, so a single-tier role is
// never delegable. An admin's session lists every tier, so this returns nearly
// every role for them — callers that want the admin view should branch on admin.
export function managedRolesUserCanDelegate(user: User | null): ManagedRole[] {
  if (!user) return [];
  return MANAGED_ROLES.filter((role) => {
    const topTier = role.tiers[role.tiers.length - 1];
    if (!topTier || role.tiers.length < 2) return false;
    return user.managedRoles?.[role.id]?.includes(topTier.id) ?? false;
  });
}
